import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';

import { errorMessage, type FieldErrorLike } from '@/types/form';

import '@/components/forms/autocomplete.css';
import { matchRanges, parseHighlight } from '@/components/forms/autocomplete/highlight';

export interface AutocompleteItem {
    label: string;
    value: string;
}

/** `N` est le nom du champ : le `setValue` appelant doit accepter une chaîne pour ce champ. */
export interface AutocompleteProps<N extends string = string> {
    name: N;
    label?: ReactNode;
    /** Suggestions possibles ; les dix premières qui correspondent à la saisie sont proposées. */
    data: AutocompleteItem[];
    setValue: (name: N, value: string) => void;
    /** Reçoit la saisie brute, pour les listes chargées à la demande. */
    setQuery?: (query: string) => void;
    defaultValue?: string;
    error?: FieldErrorLike;
    required?: boolean;
}

const escapeRegexCharacters = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getSuggestionValue = (suggestion: AutocompleteItem) => `${suggestion.label}`;

/**
 * Autocomplétion d'un champ du formulaire.
 *
 * Remplace `react-autosuggest` en reproduisant son balisage (classes `react-autosuggest__*`,
 * rôles ARIA) et son comportement : suggestions à la saisie et au focus, navigation au clavier
 * qui recopie la suggestion survolée, Entrée pour valider, Échap pour revenir à la saisie.
 */
const Autocomplete = <N extends string>({ name, label, data, setValue, setQuery, defaultValue, error, required }: AutocompleteProps<N>) => {
    const listboxId = `react-autowhatever-${useId().replace(/:/g, '')}`;
    const [value, setInputValue] = useState(defaultValue || '');
    const [suggestions, setSuggestions] = useState<AutocompleteItem[]>([]);
    const [focused, setFocused] = useState(false);
    const [highlighted, setHighlighted] = useState<number | null>(null);
    const valueBeforeUpDown = useRef<string | null>(null);
    const suggestionsRef = useRef(suggestions);
    suggestionsRef.current = suggestions;

    const getSuggestions = (input: string) => {
        const escapedValue = escapeRegexCharacters(input.trim());

        if (escapedValue === '') {
            return [];
        }

        const regex = new RegExp('\\b' + escapedValue, 'i');

        return data.filter((item) => regex.test(getSuggestionValue(item))).slice(0, 10);
    };

    // Liste chargée à la demande : les suggestions suivent les nouvelles données.
    useEffect(() => {
        setSuggestions(getSuggestions(value));
    }, [data]);

    const change = (newValue: string) => {
        setInputValue(newValue);

        if (setQuery) {
            setQuery(newValue);
        }

        const item = suggestionsRef.current.find((s) => s.label === newValue);

        if (item) {
            setValue(name, item.value);
        }
    };

    const close = () => {
        setSuggestions([]);
        setHighlighted(null);
        valueBeforeUpDown.current = null;
    };

    const isOpen = focused && suggestions.length > 0;

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                event.preventDefault();
                if (!isOpen) {
                    setSuggestions(getSuggestions(value));
                    return;
                }
                if (valueBeforeUpDown.current === null) valueBeforeUpDown.current = value;
                const count = suggestions.length;
                const current = highlighted;
                // Comme react-autosuggest : on passe par « aucune suggestion » entre la fin et le début.
                let next: number | null;
                if (event.key === 'ArrowDown') {
                    next = current === null ? 0 : current + 1 >= count ? null : current + 1;
                } else {
                    next = current === null ? count - 1 : current - 1 < 0 ? null : current - 1;
                }
                setHighlighted(next);
                change(next === null ? valueBeforeUpDown.current : getSuggestionValue(suggestions[next]!));
                break;
            }
            case 'Enter':
                if (isOpen && highlighted !== null) {
                    event.preventDefault();
                    close();
                }
                break;
            case 'Escape':
                if (isOpen) {
                    event.preventDefault();
                    if (valueBeforeUpDown.current !== null) change(valueBeforeUpDown.current);
                    close();
                } else if (value) {
                    change('');
                }
                break;
            default:
                break;
        }
    };

    const inputId = `autocomplete-${name}`;
    const errorId = `${inputId}-error`;
    const isFloating = value || focused;

    return (
        <div className={`relative w-full autocomplete ${error ? 'has-error' : ''}`}>
            <div
                role="combobox"
                aria-haspopup="listbox"
                aria-owns={listboxId}
                aria-expanded={isOpen}
                className={`react-autosuggest__container${isOpen ? ' react-autosuggest__container--open' : ''}`}
            >
                <input
                    type="text"
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-activedescendant={highlighted !== null ? `${listboxId}--item-${highlighted}` : undefined}
                    className={`react-autosuggest__input${isOpen ? ' react-autosuggest__input--open' : ''}${focused ? ' react-autosuggest__input--focused' : ''}`}
                    id={inputId}
                    placeholder={typeof label === 'string' ? label : undefined}
                    value={value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        valueBeforeUpDown.current = null;
                        setHighlighted(null);
                        change(e.target.value);
                        setSuggestions(getSuggestions(e.target.value));
                    }}
                    onFocus={() => {
                        setFocused(true);
                        setSuggestions(getSuggestions(value));
                    }}
                    onBlur={() => {
                        setFocused(false);
                        close();
                    }}
                    onKeyDown={onKeyDown}
                    aria-required={required || undefined}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                />
                <div
                    id={listboxId}
                    role="listbox"
                    className={`react-autosuggest__suggestions-container${isOpen ? ' react-autosuggest__suggestions-container--open' : ''}`}
                >
                    {isOpen && (
                        <ul role="listbox" className="react-autosuggest__suggestions-list">
                            {suggestions.map((suggestion, index) => {
                                const text = getSuggestionValue(suggestion);
                                const parts = parseHighlight(text, matchRanges(text, valueBeforeUpDown.current ?? value));

                                return (
                                    <li
                                        key={`${suggestion.value}-${index}`}
                                        role="option"
                                        id={`${listboxId}--item-${index}`}
                                        aria-selected={highlighted === index}
                                        data-suggestion-index={index}
                                        className={`react-autosuggest__suggestion${index === 0 ? ' react-autosuggest__suggestion--first' : ''}${highlighted === index ? ' react-autosuggest__suggestion--highlighted' : ''}`}
                                        // mousedown plutôt que click : le blur du champ fermerait la liste avant le clic.
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            change(text);
                                            close();
                                        }}
                                        onMouseEnter={() => setHighlighted(index)}
                                        onMouseLeave={() => setHighlighted(null)}
                                    >
                                        <span className="flex items-center">
                                            <span>
                                                {parts.map((part, i) => (
                                                    <span className={part.highlight ? 'font-semibold text-primary' : undefined} key={i}>
                                                        {part.text}
                                                    </span>
                                                ))}
                                            </span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
            <label
                htmlFor={inputId}
                className={`absolute left-3 bg-card px-1 text-muted-foreground transition-all duration-200 pointer-events-none ${isFloating ? 'text-xs -top-2' : 'text-sm top-4'} ${focused ? 'text-primary' : ''} ${error && focused ? 'text-destructive' : ''}`}
            >
                {label}
                {required ? (
                    <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                        *
                    </sup>
                ) : (
                    ''
                )}
            </label>
            {error && (
                <p id={errorId} role="alert" aria-live="assertive" className="text-xs text-destructive italic mt-1">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default Autocomplete;
